import { use, useState, useEffect } from 'react'
import { tv } from 'tailwind-variants'
import { Card } from '@/api-client/data-contracts'
import visa from './images/visa.svg'
import mastercard from './images/mastercard.svg'
import amex from './images/amex.svg'
import jcb from './images/jcb.svg'
import diners from './images/diners.svg'
import discover from './images/discover.svg'

type Props = {
  gap: 's' | 'm'
  creditCard: Promise<Card | null>
  onInitialized: (card: Card | null) => void
  onUpdate: (values: { holderName: string; numberElement: any }) => void
}

const wrapper = tv({
  variants: {
    gap: {
      s: 'space-y-5',
      m: 'space-y-8',
    },
  },
  defaultVariants: {
    gap: 's',
  },
})

export const CreditCardForm: React.FC<Props> = ({ gap, creditCard, onInitialized, onUpdate }) => {
  const card = use(creditCard)
  const [numberElement, setNumberElement] = useState(null)
  const [holderName, setHolderName] = useState('')
  const inputWrapperClass = 'border border-gray rounded-lg px-3 outline-none focus:border-black transition-colors bg-white'

  useEffect(() => {
    if (card) {
      onInitialized(card)
      return
    }

    window.payjpInstance ||= window.Payjp(window.payjpPublicKey, { threeDSecureWorkflow: 'redirect' })

    const elements = window.payjpInstance.elements()
    const style = {
      base: {
        fontFamily: '"Noto Sans JP", -apple-system, "BlinkMacSystemFont", "Hiragino Kaku Gothic ProN", Sans-Serif',
        color: '#252525',
        caretColor: '#252525',
        backgroundColor: 'white',
        fontSize: '14px',
        lineHeight: '44px',
        letterSpacing: 'normal',
        '::placeholder': {
          display: 'none',
        },
      },
    }
    const number = elements.create('cardNumber', { style, placeholder: '' })
    const expiry = elements.create('cardExpiry', { style })
    const cvc = elements.create('cardCvc', { style, placeholder: '' })

    number.mount('#card-number')
    expiry.mount('#card-expiry')
    cvc.mount('#card-cvc')

    setNumberElement(number)

    onInitialized(null)
  }, [])

  useEffect(() => onUpdate({ holderName, numberElement }), [holderName, numberElement])

  return card ? (
    <>
      <p className="">クレジットカード</p>
      <span className="">**** **** **** {card.attributes.last4}</span>
    </>
  ) : (
    <>
      <div className="flex gap-2 mb-6 items-center">
        {[
          { name: 'Visa', image: visa },
          { name: 'Mastercard', image: mastercard },
          { name: 'Amex', image: amex },
          { name: 'JCB', image: jcb },
          { name: 'DinersClub', image: diners },
          { name: 'Discover', image: discover },
        ].map((card) => (
          <img key={card.name} src={card.image} alt={card.name} className="h-6 w-auto" />
        ))}
      </div>
      <div className={wrapper({ gap })}>
        <div>
          <label htmlFor="card-number" className="block text-[12px] text-dark-gray mb-2">
            カード番号 <span className="text-accent">*</span>
          </label>
          <div className={inputWrapperClass} id="card-number" />
        </div>
        <div>
          <label htmlFor="card-holder-name" className="block text-[12px] text-dark-gray mb-2">
            カード名義
          </label>
          <input
            id="card-holder-name"
            type="text"
            className="w-full border border-gray rounded-lg px-3 py-3 text-[14px] outline-none bg-white"
            onChange={(e) => setHolderName(e.target.value)}
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label htmlFor="card-expiry" className="block text-[12px] text-dark-gray mb-2">
              有効期限 <span className="text-accent">*</span>
            </label>
            <div className={inputWrapperClass} id="card-expiry" />
          </div>
          <div className="flex-1">
            <label htmlFor="card-cvc" className="block text-[12px] text-dark-gray mb-2">
              セキュリティコード <span className="text-accent">*</span>
            </label>
            <div className={inputWrapperClass} id="card-cvc" />
          </div>
        </div>
      </div>
    </>
  )
}
